import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Spinner, useEnvironment } from "@keycloak/keycloak-ui-shared";
import { getCredentials } from "../../api/methods";
import {
  CredentialContainer,
  CredentialMetadataRepresentation,
} from "../../api/representations";
import { Page } from "../../components/page/Page";
import { TFuncKey } from "../../i18n";
import { usePromise } from "../../utils/usePromise";
import sha256 from 'crypto-js/sha256';
import Base64 from 'crypto-js/enc-base64';
import { v4 as uuid } from 'uuid';
import {Environment, environment} from "../../environment";
import {usePersonalInfoContext} from "../../providers/PersonalInfoProvider";
import Card from "../../components/card/Card";
import Button from "../../components/button/Button";
import React from "react";
import { cardContentStyles, cardStyles, corporateAccountLinkStyles, passwordButtonStyles, signInBoxStyles } from "./styles";
import InfoMessage from "./components/InfoMessage";
import { Box, Link } from "@mui/material";
import { withDivider } from "../../hoc/withDivider";
import Modal from "../../components/modal/Modal";
import { useAccountAlerts } from "../../utils/useAccountAlerts";
import SetupPersonalMethod from './components/SetupPersonalMethod';
import PasswordNotSetup from './components/PasswordNotSetup';
import CredentialRow from './components/CredentialRow';
import ExternalIcon from "../../icons/External";

export const SigningIn = () => {
  const { t } = useTranslation();
  const context = useEnvironment<Environment>();
  const { login } = context.keycloak;
  const {accountType, profileType, email } = usePersonalInfoContext();
  const [credentials, setCredentials] = useState<CredentialContainer[]>();
  const { addAlert, addError } = useAccountAlerts();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalCredentionalId, setModalCredentionalId] = useState<string>();

  function generateCodeChallenge(codeVerifier: string) {
    return base64URL(sha256(codeVerifier));
  }

  function base64URL(string: any) {
    return string.toString(Base64).replace(/=/g, '').replace(/\+/g, '-').replace(/\//g, '_');
  }

  function fixedEncodeURIComponent(str: string) {
    return encodeURIComponent(str).replace(/[!'()*]/g, function (c) {
      return '%' + c.charCodeAt(0).toString(16);
    });
  }

  const redirectToAttach = () => {
      const nonce = uuid();
      const userSessionStateId = environment.userSessionStateId;
      const clientId = environment.clientId;
      const redirectUri = environment.baseUrl;

      const initialString = nonce + userSessionStateId + clientId + "email";
      const encodedString = generateCodeChallenge(initialString);
      const hash = fixedEncodeURIComponent(encodedString);

      window.location.href =
        '/auth/realms/plusx/user/attach/email?client_id=' + clientId +'&redirect_uri=' + redirectUri + '&nonce=' + nonce + '&hash=' + hash
  };

  usePromise(
    (signal) => getCredentials({ signal, context }),
    setCredentials,
    [],
  );

  const handleModalClose = () => {
    setIsModalOpen(false);
    setModalCredentionalId(undefined);
  };

  const handleModalRemove = () => {
    if(modalCredentionalId) {
      login({
        action: modalCredentionalId,
      }).then(() => {
        addAlert(t('removePersonalSignInSuccess'));
      }).catch((err) => {
        addError(t('somethingWentWrongDescription'), err);
      });
    }
  };

  if (!credentials) {
    return <Spinner />;
  }

  const credentialUniqueCategories = [
    ...new Set(credentials.filter((c) => c.category != "two-factor").map((c) => c.category)),
  ];

  return (
    <Page title={t("signingIn")}>
     <>
      {credentialUniqueCategories.map((category) => (
        <React.Fragment key={category}>
          {credentials
            .filter((cred) => cred.category == category)
            .map(item => ({
              ...item,
              userCredentialMetadatas: [{ email: { userEmail: email } }, ...(item.userCredentialMetadatas || [])] as unknown as CredentialMetadataRepresentation[]
            }))
            .map((container) => (
              <React.Fragment key={category}>

                {container.createAction && !container.userCredentialMetadatas.slice(1).length && (accountType == "CORPORATE" && profileType === "2") ? (
                  <SetupPersonalMethod
                    key={container.category}
                    category={category}
                    t={t}
                    container={container}
                    resourceUrl={context.environment.resourceUrl}
                    redirectToAttach={redirectToAttach} />
                ) : (
                  <Card
                  title={t(category as TFuncKey)}
                  withHeadBorder
                  sxContent={cardContentStyles}
                  aria-label="credential list"
                  data-testid={`${container.type}/credential-list`}
                  sxCard={cardStyles}
                  action={
                    container.removeable && accountType != "CORPORATE" && (
                      <Button
                        data-testrole="remove"
                        variant="outlined"
                        size="small"
                        sx={passwordButtonStyles}
                        onClick={() => {
                          setIsModalOpen(true);
                          setModalCredentionalId(`delete_credential:${
                              container.userCredentialMetadatas[1]?.credential?.id}`)
                        }}
                      >
                        {t('remove')}
                      </Button>
                    )
                  }
                >

                  <Box sx={signInBoxStyles}>
                    {container.userCredentialMetadatas.map((meta: any, index) => (
                      withDivider(CredentialRow)({
                        t: t,
                        meta: meta,
                        items: container.userCredentialMetadatas,
                        index,
                        container: container,
                        login: login,
                        accountType: accountType,
                        updateAction: container.updateAction,
                        showDivider: index < container.userCredentialMetadatas.length - 1,
                      })
                    ))}

                    {!container.userCredentialMetadatas.find(item => Object.keys(item).includes('credential')) && (
                      <PasswordNotSetup t={t} container={container} accountType={accountType} login={login} />
                    )}

                  </Box>
                  <InfoMessage message={t('managePersonalSigningMethod')} />
                </Card>
                )}

              </React.Fragment>
            ))}
        </React.Fragment>
      ))}
      {accountType !== "PUBLIC" && (
        <Card title={t("corp-authentication")} withHeadBorder>
          <Link
            sx={corporateAccountLinkStyles}
            component="button"
            onClick={() => window.open("https://myaccount.microsoft.com/", "_blank")}
          >
            <ExternalIcon />
            {t("changeCorpAzureAccount")}
          </Link>
        </Card>
      )}
     </>
      <Modal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        title={t("removePersonalSignInMethod")}
        confirmText={t("remove")}
        onConfirm={handleModalRemove}
      >
        {t("removePersonalSignInDesc")}
      </Modal>
    </Page>
  );
};

export default SigningIn;
